import clipboardTypeIcon from '../assets/clipboard-type.svg';
import fileInputIcon from '../assets/file-input.svg';
import imageIcon from '../assets/image.svg';
import stlIcon from '../assets/stl.svg';
import type { Match, PerMessageMatches } from '../types/search';
import { isAiEmail } from './messageAuthor';

/**
 * Hardcoded set of ODO orthodontist emails. Messages from these authors get
 * a "Dr" prefix on the display name plus a visual accent in MessageBubble.
 * Add new emails here as orthos join the team.
 */
const ORTHO_EMAILS: ReadonlySet<string> = new Set([
    'johnsr@warfordortho.com',
    'john@ondemandorthodontist.com',
    'robert@ondemandorthodontist.com',
    'gabriela@ondemandorthodontist.com',
    'c.celaya@ondemandorthodontist.com',
    'ariana.m@ondemandorthodontist.com',
    'a.leon-corrales@ondemandorthodontist.com'
]);

export const isOrtho = (email: string | undefined): boolean => {
    if (!email) return false;
    return ORTHO_EMAILS.has(email.toLowerCase());
};

export const formatTimestamp = (iso: string): string => {
    const d = new Date(iso);
    const now = new Date();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const isToday = d.toDateString() === now.toDateString();
    return `${isToday ? 'Today' : d.toLocaleDateString()} \u2022 ${time}`;
};

// Matches a leading "Dr" / "Dr." / "dr " (any case) so we don't double-prefix
// a name that already carries the title from its WordPress profile.
const DR_PREFIX_REGEX = /^dr\.?\s/i;

export const displayName = (name: string | undefined, email?: string | undefined): string => {
    if (email && isAiEmail(email)) return 'ODO AI';
    const trimmed = name?.trim() || 'You';
    if (email && isOrtho(email) && !DR_PREFIX_REGEX.test(trimmed)) {
        return `Dr ${trimmed}`;
    }
    return trimmed;
};

export const getFileIcon = (fileName: string): string => {
    const ext = fileName.toLowerCase().split('.').pop() || '';
    if (['stl'].includes(ext)) return stlIcon;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return imageIcon;
    if (['txt', 'text', 'md', 'markdown', 'rtf', 'pdf'].includes(ext)) return clipboardTypeIcon;
    return fileInputIcon;
};

const MAX_FILE_NAME_DISPLAY = 42;

export const truncateFileName = (name: string, maxLen: number = MAX_FILE_NAME_DISPLAY): string => {
    if (!name || name.length <= maxLen) return name;
    const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
    const base = name.slice(0, name.length - ext.length);
    if (base.length <= 4) return name.slice(0, maxLen - 3) + '...';
    return base.slice(0, maxLen - ext.length - 3) + '...' + ext;
};

export const buildSearchMatches = (
    messages: Array<{ text?: string }>,
    query: string
): { perMessageMatches: PerMessageMatches; flatMatchesCount: number } => {
    const perMsg: PerMessageMatches = new Map();
    let flatCount = 0;
    const needle = query.trim().toLowerCase();
    if (!needle) return { perMessageMatches: perMsg, flatMatchesCount: 0 };

    const nLen = needle.length;
    messages.forEach((m: any, msgIndex: number) => {
        const raw = m?.text || m?.htmlBody || '';
        const msgText = raw.replace(/<[^>]*>/g, '').toString();
        if (!msgText) return;
        const hay = msgText.toLowerCase();
        let from = 0;
        for (;;) {
            const found = hay.indexOf(needle, from);
            if (found === -1) break;
            const match: Match = { start: found, end: found + nLen, globalIdx: flatCount };
            const arr = perMsg.get(msgIndex) || [];
            arr.push(match);
            perMsg.set(msgIndex, arr);
            flatCount += 1;
            from = found + nLen;
        }
    });

    return { perMessageMatches: perMsg, flatMatchesCount: flatCount };
};

export const messagePreviewText = (text: string | null | undefined): string => {
    if (!text) return '';
    const stripped = text.replace(/<[^>]*>/g, '').trim();
    if (stripped) return stripped;
    if (/<img\s/i.test(text)) return 'Sent an image';
    return 'Sent a message';
};
